/** @jsxImportSource @emotion/react */
import { Spinner } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { ProductsAPI } from "../../../api/Products";
import { StripeAPI } from "../../../api/Stripe";
import { TagApi } from "../../../api/Tags";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import {
  IProduct,
  IStripeIntegration,
  ProductType,
} from "../../../generated/Api";
import { SettingsTab } from "../../../pages/settings";
import { IOption } from "../../../types/CommonTypes";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import { AllIntegrationsAtom } from "./Integrations";
import { StripeProductItem } from "./StripeProductItem";

const Container = styled.div`
  width: 1000px;
  height: fit-content;

  border: 1px solid #eaeaeabf;

  background-color: #ffffff;

  margin: 40px 0px;

  padding: 24px;

  cursor: default;
`;

const InfoColumn = styled.div`
  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 16px;

  display: flex;
  align-items: stretch;
  justify-content: space-between;

  > img {
    width: 67px;
    height: 67px;
  }
`;

const TextColumn = styled.div`
  max-width: 600px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 18px;
  color: #222222;
  font-weight: 700;
  margin-bottom: 4px;
`;

const SubTitle = styled.div`
  font-size: 16px;
  color: #707070;
`;

const FormWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: start;

  margin: 32px 0;

  grid-gap: 24px;
`;

const StyledAutocompleteSelectWrapper = styled.div`
  display: flex;
  flex-direction: row;

  width: 5000px;
  grid-gap: 10px;
`;

const ProductListContent = styled.div`
  border-top: 1px solid #eaeaeabf;
  padding-top: 16px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;

  width: 100%;

  grid-gap: 32px;
`;

const ProductListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  width: 100%;

  grid-gap: 16px;

  > span {
    font-size: 20px;
    color: #707070;
    font-weight: 600;
  }
`;

const FooterColumn = styled.div`
  border-top: 1px solid #eaeaeabf;

  padding-top: 16px;

  margin-top: 16px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

const EVENTS = [
  { value: "trialing", name: "Trialing" },
  { value: "active", name: "Active" },
  { value: "incomplete", name: "Incomplete" },
  { value: "incomplete_expired", name: "Incomplete Expired" },
  { value: "canceled", name: "Canceled" },
  { value: "past_due", name: "Past Due" },
  { value: "recovery-from-past_due", name: "Recovery from Past Due" },
  { value: "unpaid", name: "Unpaid" },
];

interface Props {
  handleSelected: (vle: SettingsTab) => void;
}

export default function StripeProducts(props: Props): JSX.Element | null {
  const { handleSelected } = props;
  const user = useRecoilValue(UserAtom);

  const modal = useModal();

  const setTagsPool = useSetRecoilState(TagsAtom);

  const allIntegrations = useRecoilValue(AllIntegrationsAtom);

  const [isLoading, setIsLoading] = useState(true);

  const [selectedProduct, setSelectProduct] = useState<IOption>({
    name: "",
    value: "",
  });

  const [selectedEvent, setSelectEvent] = useState<IOption>({
    name: "",
    value: "",
  });

  const [integrationProducts, setIntegrationProducts] = useState<IProduct[]>(
    []
  );
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IOption[]
  >([]);

  const stripeIntegration = useMemo(
    () => allIntegrations.find((i) => i.type === "STRIPE"),
    [allIntegrations]
  ) as IStripeIntegration;

  async function getTags() {
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  async function getProducts() {
    const [stripeProductsAux, integrationProductsAux] = await Promise.all([
      StripeAPI.getProducts(),
      ProductsAPI.get("STRIPE"),
    ]);

    const stripeProductsFiltered = stripeProductsAux.filter(
      (p) => !integrationProductsAux.find((ip) => ip.productId === p.id)
    );

    setAutoCompleteSuggestions(
      stripeProductsFiltered.map((p) => ({ value: p.id, name: p.name }))
    );

    setIntegrationProducts(integrationProductsAux);

    setIsLoading(false);
  }

  async function disconnectIntegration() {
    if (process.browser && stripeIntegration._id) {
      await IntegrationAPI.disconnect(stripeIntegration._id);
      handleSelected(SettingsTab.STRIPE);
    }

    modal.popModal();
  }

  function handleDisconnectClick() {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Disconnect Integration"
        description="Are you sure you want to disconnect this integration?"
        buttonsName={["No", "Yes"]}
        onOkClicked={disconnectIntegration}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }
  function handleSelectedProduct(productOption: IOption) {
    setSelectProduct(productOption);
  }

  function handleSelectedEvent(eventOption: IOption) {
    setSelectEvent(eventOption);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (!selectedProduct.value) return;

      const productId = `${selectedEvent.value} -> ${selectedProduct.value}`;

      if (integrationProducts.find((p) => p.productId === productId)) {
        toast("This product already exists", { type: "error" });
        return;
      }

      const createdProduct = await ProductsAPI.create({
        productName: `${selectedEvent.name} -> ${selectedProduct.name}`,
        productId: productId,
        userUid: user.userUid,
        type: ProductType.STRIPE,
        tagIds: [],
      });

      if (!createdProduct) return;

      setIntegrationProducts([...integrationProducts, createdProduct]);
      // setAutoCompleteSuggestions((prev) =>
      //   prev.filter((p) => p.value !== selectedProduct.value)
      // );

      setSelectProduct({
        name: "",
        value: "",
      });

      setSelectEvent({
        name: "",
        value: "",
      });
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getTags();
    getProducts();
  }, []);

  useEffect(() => {
    if (!stripeIntegration || stripeIntegration.type !== "STRIPE")
      handleSelected(SettingsTab.INTEGRATIONS);
  }, [stripeIntegration]);

  const IntegrationProducts = useMemo(
    () =>
      integrationProducts.length ? (
        integrationProducts.map((product, index) => (
          <StripeProductItem
            key={product._id}
            product={product}
            refreshProducts={async () => {
              await getProducts();
            }}
          />
        ))
      ) : (
        <span>No products found</span>
      ),
    [integrationProducts]
  );

  return (
    <Container>
      <InfoColumn>
        <TextColumn>
          <Title>Stripe Products Integration</Title>
          <SubTitle>
            Please choose the products you wish to add tags from the list below.
          </SubTitle>
        </TextColumn>

        <img src="./images/stripe.png" alt="stripe" />
      </InfoColumn>

      <FormWrapper>
        <StyledAutocompleteSelectWrapper>
          <StyledAutocompleteSelect
            options={autoCompleteSuggestions}
            onOptionSelect={handleSelectedProduct}
            selectedOption={selectedProduct}
            placeholder="Enter the product name from Stripe"
          />
          <StyledAutocompleteSelect
            options={EVENTS}
            onOptionSelect={handleSelectedEvent}
            selectedOption={selectedEvent}
            placeholder="Select the event"
          />
        </StyledAutocompleteSelectWrapper>

        <StyledButton onClick={handleSubmit}>Add</StyledButton>
      </FormWrapper>

      <ProductListContent>
        <Title>Products</Title>
        <ProductListWrapper>
          {isLoading ? (
            <Spinner size="lg" marginBottom={3} />
          ) : (
            IntegrationProducts
          )}
        </ProductListWrapper>
      </ProductListContent>

      <FooterColumn>
        <StyledButton
          css={css`
            background-color: transparent;
            border-color: red;
            color: red;

            &:hover {
              background-color: red;
              color: white;
            }

            transition: 300ms;
          `}
          onClick={handleDisconnectClick}
        >
          Disconnect integration
        </StyledButton>
      </FooterColumn>
    </Container>
  );
}
